import React from 'react';
import {BrowserRouter as Router, Routes, Route, useNavigate, useLocation, NavigateFunction, Location} from 'react-router-dom';
import Navigation, * as MYROUTES from './navigation';
import { UserPane, SignInStatus } from './components/userpane';
import { ContentStatus } from './types/content.types';
import { TaskStatus } from './types/tasks.types';

import Monitoring from './monitoring';

import {Home} from './pages/home/home';
import ContentSets from './pages/content-sets/content-sets';
import ContentOne, { getEmptyContentData } from './pages/content-one/content-one';
import TasksList from './pages/tasks-list/tasks-list';
import TasksCreate from './pages/tasks-create/tasks-create';
import TasksView, { getEmptyTasksData } from './pages/tasks-view/tasks-view';
import GetLink from './pages/getlink/getlink';
import Embed from './pages/embed/embed';
import { ContentSetData, ContentSetActions } from './types/content.types';
import { TaskData, TaskDataMeta, TaskControlsAction, TaskListItem, createTaskListItem } from './types/tasks.types';

import FBWrapper, {MyUserType} from './firebase/fbwrapper';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthResult } from './firebase/fbinterface';

interface StateType{
	user:MyUserType;
	tasksList:TaskListItem[];
	userContentList:string[];
	oneTasksData:TaskData;
	contentSetData:ContentSetData;
	isContentSetNew:boolean;
	isEdited:boolean;
	taskStatus:TaskStatus;
	contentStatus:ContentStatus;
	signInStatus:SignInStatus;
}

interface PropsType{
	navigate: NavigateFunction;
	location: Location;
}

class AppClass extends React.Component<PropsType>{

	public state:StateType;
	private firebaseWrapper:FBWrapper;
	private taskClickedTitle:string='';//used when click on a diff task while waiting for taskdata reset
	private contentClickedTitle:string='';
	private authListenerAdded:boolean=false;
	private navigate: NavigateFunction;
	private monitoring:Monitoring;
	private routeChangeScrollY: number | null = null;
	private currentMonitoredRoute: string = '';

	constructor(props:PropsType){
		super(props);
		this.navigate = props.navigate;

		this.signInClick=this.signInClick.bind(this);
		this.signOutClick=this.signOutClick.bind(this);
		this.getTask=this.getTask.bind(this);
		this.taskControlsClick=this.taskControlsClick.bind(this);
		this.taskCreateClick=this.taskCreateClick.bind(this);
		this.resetTaskStatus=this.resetTaskStatus.bind(this);
		this.getContentSet=this.getContentSet.bind(this);
		this.newContentSet=this.newContentSet.bind(this);
		this.updateContentSet=this.updateContentSet.bind(this);
		this.contentSetMenuClick=this.contentSetMenuClick.bind(this);

		this.state={
			tasksList:[],
			userContentList:[],
			oneTasksData:getEmptyTasksData(),
			contentSetData:getEmptyContentData(),
			isContentSetNew:true,
			isEdited:false,
			taskStatus:TaskStatus.Default,
			contentStatus:ContentStatus.Default,
			user:{username:'', usermail:''},
			signInStatus:SignInStatus.No
		};

		this.firebaseWrapper=new FBWrapper();
		this.monitoring=new Monitoring();
	}

	componentDidMount(){
		this.addAuthListener();
	}

	componentDidUpdate(prevProps:PropsType){
		// On route change, save scroll position only if at bottom of page
		if(this.props.location.pathname !== prevProps.location.pathname){
			const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
			const atBottom = window.scrollY >= maxScroll - 1;

			if(atBottom){
				this.routeChangeScrollY = window.scrollY;
				this.currentMonitoredRoute = this.props.location.pathname;
			} else {
				this.routeChangeScrollY = null;
				this.currentMonitoredRoute = '';
			}
		}

		// If monitoring this route and scroll increased, restore it
		if(this.currentMonitoredRoute === this.props.location.pathname &&
		   this.routeChangeScrollY !== null &&
		   window.scrollY > this.routeChangeScrollY){
			window.scrollTo(0, this.routeChangeScrollY);
		}
	}

	////////////////////////////////////////////////////////////////////////////
	//login

	addAuthListener(){
		if(this.authListenerAdded===false){
			this.authListenerAdded=true;
			this.firebaseWrapper.addAuthListener((res:MyUserType)=>{
				if(res.username!==''){
					this.setState({user:res,signInStatus:SignInStatus.Yes});
					this.getTaskList();
					this.getContentList();
				}
			});
		}
	}

	async signInClick(){
		if(this.state.signInStatus===SignInStatus.Yes)return;
		if(this.state.signInStatus===SignInStatus.Trying)return;
		this.setState({signInStatus:SignInStatus.Trying});
		const response = await this.firebaseWrapper.signIn();
		if(response.result !== AuthResult.Success){
			this.setState({signInStatus:SignInStatus.InFailed});
		}
	}

	async signOutClick(){
		if(this.state.signInStatus!==SignInStatus.Yes)return;
		this.setState({signInStatus:SignInStatus.No, user:{username:'', usermail:''},tasksList:[],userContentList:[],oneTasksData:getEmptyTasksData(),contentSetData:getEmptyContentData(),isContentSetNew:true});
		const response = await this.firebaseWrapper.signOut();
		if(response.result !== AuthResult.Success){
			this.setState({signInStatus:SignInStatus.OutFailed});
		}
	}

	////////////////////////////////////////////////////////////////////////////
	//content

	async getContentList(){
		this.setState({contentStatus:ContentStatus.GetList});
		const {status, contentList} = await this.firebaseWrapper.getContentList();
		this.setState({userContentList:contentList, contentStatus:status});
	}

	async getContentSet(contentSet:string){
		//prevent repeated clicking
		if(contentSet===this.contentClickedTitle&&this.state.contentStatus===ContentStatus.GetSet)return;

		this.contentClickedTitle=contentSet;
		let newEmptySet=getEmptyContentData();
		newEmptySet.title=contentSet;
		this.setState({contentSetData:newEmptySet, contentStatus:ContentStatus.GetSet, isContentSetNew:false, isEdited:false});
		this.navigate(MYROUTES.CONTENT_ONE);
		const {status, contentSetData} = await this.firebaseWrapper.getContentSet(contentSet);
		if(contentSetData.title===this.contentClickedTitle){
			this.contentClickedTitle='';
			this.setState({contentSetData, contentStatus:status});
		}
	}

	newContentSet(){
		this.contentClickedTitle='';
		this.setState({contentSetData:getEmptyContentData(), isContentSetNew:true, contentStatus:ContentStatus.Default, isEdited:true});
		this.navigate(MYROUTES.CONTENT_ONE);
	}

	updateContentSet(newContentSetData:ContentSetData){
		this.setState({contentSetData:newContentSetData, isEdited:true});
	}

	contentSetMenuClick(contentSetAction:ContentSetActions, contentSet:string){
		if(contentSetAction===ContentSetActions.Delete)this.deleteUserContentSet(contentSet);
		if(contentSetAction===ContentSetActions.Save)this.saveUserContentSet();
		if(contentSetAction===ContentSetActions.Discard)this.setState({contentSetData:getEmptyContentData()});
	}

	async saveUserContentSet(){
		let isNew=this.state.isContentSetNew;
		
		this.setState({isContentSetNew:false,contentStatus:ContentStatus.SaveSet});
		const {status, userContentList: newUserContentList, newID: setID} = await this.firebaseWrapper.saveContentSet(this.state.contentSetData, this.state.userContentList);
		
		this.monitoring.saveContentSetOutcome(status,isNew,this.state.user.usermail,this.state.contentSetData,setID);

		let newContentSetData = {...this.state.contentSetData,myID: setID};
		this.setState({userContentList:newUserContentList,contentStatus:status, isEdited:false, contentSetData:newContentSetData});
	}

	async deleteUserContentSet(contentSet:string){
		if(this.state.userContentList.indexOf(contentSet)===-1)return;

		this.setState({contentStatus:ContentStatus.DeleteSet});
		const {status, userContentList: newUserContentList} = await this.firebaseWrapper.deleteContentSet(contentSet, this.state.userContentList);
		if(status===ContentStatus.DeleteSetError){
			this.setState({userContentList:newUserContentList, contentStatus:status});
		}else{
			this.setState({contentSetData:getEmptyContentData(), isContentSetNew:true, userContentList:newUserContentList, contentStatus:status});
			if(this.props.location.pathname === MYROUTES.CONTENT_ONE){
				this.navigate(MYROUTES.CONTENT);
			}
		}
	}

	////////////////////////////////////////////////////////////////////////////
	//tasks

	async getTaskList(){
		this.setState({taskStatus:TaskStatus.GetList});
		const {status, tasksList} = await this.firebaseWrapper.getTaskList();
		const taskListItems = tasksList.map(title => createTaskListItem(title));
		this.setState({tasksList: taskListItems, taskStatus:status});

		// Load meta in background (non-blocking)
		this.loadTasksMeta(taskListItems);
	}

	async loadTasksMeta(taskItems: TaskListItem[]){
		const taskTitles = taskItems.map(item => item.taskTitle);
		const metaMap = await this.firebaseWrapper.getTasksMetaBatch(taskTitles);

		const updatedTasksList = taskItems.map(item => {
			const meta = metaMap.get(item.taskTitle);
			if(meta) {
				return { ...item, meta };
			}
			return item;
		});
		this.setState({tasksList: updatedTasksList});
	}

	async getTask(taskTitle:string){
		if(taskTitle===''){
			this.navigate(MYROUTES.TASKS_CREATE);
			return;
		}

		//prevent repeated clicking
		if(taskTitle===this.taskClickedTitle&&this.state.taskStatus===TaskStatus.GetTask)return;

		const taskExists = this.state.tasksList.some(t => t.taskTitle === taskTitle);
		if(taskExists){
			this.navigate(MYROUTES.TASKS_VIEW);
			this.taskClickedTitle=taskTitle;
			this.setState({oneTasksData:getEmptyTasksData(),taskStatus:TaskStatus.GetTask});
			const {status, taskData} = await this.firebaseWrapper.getTaskData(taskTitle);
			this.setState({oneTasksData:taskData, taskStatus:status});
		}
	}

	taskControlsClick(taskControlsAction:TaskControlsAction, taskTitle:string){
		if(taskControlsAction===TaskControlsAction.Delete)this.deleteTask(taskTitle);
		if(taskControlsAction===TaskControlsAction.Reset)this.resetTaskData(taskTitle);
	}

	async deleteTask(taskTitle:string){
		const taskExists = this.state.tasksList.some(t => t.taskTitle === taskTitle);
		if(!taskExists)return;

		this.setState({oneTasksData:getEmptyTasksData(),taskStatus:TaskStatus.DeleteTask});
		const taskTitles = this.state.tasksList.map(t => t.taskTitle);
		const {status, tasksList} = await this.firebaseWrapper.deleteTask(taskTitle, taskTitles);
		const taskListItems = tasksList.map(title => {
			const existing = this.state.tasksList.find(t => t.taskTitle === title);
			return existing || createTaskListItem(title);
		});
		this.setState({tasksList: taskListItems, taskStatus:status});
		if(this.props.location.pathname === MYROUTES.TASKS_VIEW){
			this.navigate(MYROUTES.TASKS_LIST);
		}
	}
  
	async resetTaskData(taskTitle:string){
		this.setState({oneTasksData:getEmptyTasksData(),taskStatus:TaskStatus.ResetTask});
		const taskStatus = await this.firebaseWrapper.resetTaskData(taskTitle);
		if(taskTitle===this.taskClickedTitle){
			if(taskStatus===TaskStatus.ResetTaskFailed){
				this.setState({taskStatus});
			}else{
				this.getTask(taskTitle);
			}
		}
	}

	async taskCreateClick(taskCreateData:TaskDataMeta){
		this.setState({taskStatus:TaskStatus.Creating});
		const taskTitles = this.state.tasksList.map(t => t.taskTitle);
		const {status, tasksList: newTaskList} = await this.firebaseWrapper.createTask(taskCreateData, taskTitles);

		this.monitoring.taskCreateClickOutcome(status,taskCreateData);
		const taskListItems = newTaskList.map(title => {
			// If this is the newly created task, include its meta
			if(title === taskCreateData.taskTitle){
				return {taskTitle: title, meta: taskCreateData};
			}
			// Preserve existing meta for other tasks
			const existing = this.state.tasksList.find(t => t.taskTitle === title);
			return existing || createTaskListItem(title);
		});
		this.setState({tasksList: taskListItems, taskStatus:status});
	}

	resetTaskStatus(){
		this.setState({taskStatus:TaskStatus.Default});
	}

	////////////////////////////////////////////////////////////////////////////

	render(){
		let isLoggedIn=this.state.user.username!=='';
		return (
			<>
				<UserPane
					username={this.state.user.username}
					signIn={this.signInClick}
					signOut={this.signOutClick}
					signInStatus={this.state.signInStatus}
				/>
				<Navigation
					isLoggedIn={isLoggedIn}
				/>
				<Routes>
					<Route path={MYROUTES.HOME} element={
						<Home
							isLoggedIn={isLoggedIn}
							username={this.state.user.username}
							taskCount={this.state.tasksList.length}
							contentSetCount={this.state.userContentList.length}
						/>} />
					<Route path={MYROUTES.CONTENT} element={
						<ContentSets
							usermail={this.state.user.usermail}
							contentStatus={this.state.contentStatus}
							userContentList={this.state.userContentList}
							getContentSet={this.getContentSet}
							newContentSet={this.newContentSet}
							contentSetMenuClick={this.contentSetMenuClick}
						/>} />
					<Route path={MYROUTES.CONTENT_ONE} element={
						<ContentOne
							usermail={this.state.user.usermail}
							isContentSetNew={this.state.isContentSetNew}
							isEdited={this.state.isEdited}
							contentStatus={this.state.contentStatus}
							userContentList={this.state.userContentList}
							contentSetData={this.state.contentSetData}
							updateContentSet={this.updateContentSet}
							contentSetMenuClick={this.contentSetMenuClick}
						/>} />
					<Route path={MYROUTES.TASKS_LIST} element={
						<TasksList
							taskStatus={this.state.taskStatus}
							taskControlsClick={this.taskControlsClick}
							usermail={this.state.user.usermail}
							tasksList={this.state.tasksList}
							getTask={this.getTask}
						/>} />
					<Route path={MYROUTES.TASKS_CREATE} element={
						<TasksCreate
							usermail={this.state.user.usermail}
							taskStatus={this.state.taskStatus}
							tasksList={this.state.tasksList}
							userContentList={this.state.userContentList}
							taskCreateClick={this.taskCreateClick}
							resetTaskStatus={this.resetTaskStatus}
						/>} />
					<Route path={MYROUTES.TASKS_VIEW} element={
						<TasksView
							usermail={this.state.user.usermail}
							oneTasksData={this.state.oneTasksData}
							taskStatus={this.state.taskStatus}
							taskControlsClick={this.taskControlsClick}
						/>} />
					<Route path={MYROUTES.GETLINK} element={
						<GetLink
							usermail={this.state.user.usermail}
							userContentList={this.state.userContentList}
						/>} />
					<Route path={MYROUTES.EMBED} element={
						<Embed
							usermail={this.state.user.usermail}
							userContentList={this.state.userContentList}
						/>} />
					</Routes>
			</>
		);
	}
}

function AppWithNavigate() {
	const navigate = useNavigate();
	const location = useLocation();
	return <AppClass navigate={navigate} location={location} />;
}

export default function App() {
	return (
		<ErrorBoundary>
			<div id="teachers-app">
				<Router basename={'/teachers-section'}>
					<AppWithNavigate />
				</Router>
			</div>
		</ErrorBoundary>
	);
}
